/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Saturday, 4th May 2019
 *
 *  Company CheckIn View
 */
import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import MaterialTable from 'material-table';
import { useStores } from 'stores/RootStore';

// Material UI
import Avatar from '@material-ui/core/Avatar';
import PersonIcon from '@material-ui/icons/PersonRounded';
import Grid from '@material-ui/core/Grid';
import AddIcon from '@material-ui/icons/AddBox';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import DetailPanelIcon from '@material-ui/icons/ChevronRight';
import EditIcon from '@material-ui/icons/Edit';
import ExportIcon from '@material-ui/icons/SaveAlt';
import FilterIcon from '@material-ui/icons/FilterList';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import NextPageIcon from '@material-ui/icons/ChevronRight';
import PreviousPageIcon from '@material-ui/icons/ChevronLeft';
import ResetSearchIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import SortArrowIcon from '@material-ui/icons/ArrowDownward';
import ThirdStateCheckIcon from '@material-ui/icons/Remove';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';

const BadgeHistory = props => {
  const { classes, theme } = props;
  const stores = useStores();
  const { isBadgeHistoryLoading, badgeHistory } = stores.companyStore;

  // Update AppStore Page View
  stores.appStore.setView('Badge History');

  const columns = [
    {
      field: 'checkInDtTm',
      title: 'checkInDateTime',
      defaultSort: 'desc',
      hidden: true,
    },
    {
      field: 'location.name',
      title: 'Location',
    },
    {
      sorting: false,
      editable: 'never',
      removable: false,
      hidden: isWidthDown('xs', props.width),
      render: rowData =>
        rowData.user !== null &&
        rowData.user.avatarUrl !== undefined &&
        rowData.user.avatarUrl !== '' &&
        rowData.user.avatarUrl !== null ? (
          <Avatar alt='Avatar' src={rowData.user.avatarUrl} className={classes.avatar} />
        ) : rowData.user !== null && rowData.user.initials !== '' ? (
          <Avatar alt='Avatar' className={classes.avatarInitials}>
            {rowData.user.initials}
          </Avatar>
        ) : (
          <PersonIcon color='inherit' fontSize='large' className={classes.avatarIcon} />
        ),
      cellStyle: {
        margin: '0',
        width: '40px',
        paddingLeft: '10px',
        paddingRight: '5px',
        textAlign: 'center',
      },
    },
    {
      field: 'displayName',
      title: 'Name',
    },
    {
      field: 'vendorCompany',
      title: 'Vendor',
    },
    {
      field: 'checkInDt',
      title: 'Check-In Date',
    },
    {
      field: 'checkInTm',
      title: 'Check-In Time',
    },
  ];

  return (
    <div className={classes.root}>
      <Grid container justify='center' alignContent='flex-start' direction='column'>
        <Grid item xs={12}>
          <MaterialTable
            title='Badge History'
            isLoading={isBadgeHistoryLoading}
            columns={columns}
            data={badgeHistory}
            style={{
              boxShadow: 'none',
              backgroundColor: 'inherit',
            }}
            icons={{
              Add: React.forwardRef((props, ref) => <AddIcon {...props} ref={ref} />),
              Check: React.forwardRef((props, ref) => <CheckIcon {...props} ref={ref} />),
              Clear: React.forwardRef((props, ref) => <ClearIcon {...props} ref={ref} />),
              Delete: React.forwardRef((props, ref) => <DeleteIcon {...props} ref={ref} />),
              DetailPanel: React.forwardRef((props, ref) => <DetailPanelIcon {...props} ref={ref} />),
              Edit: React.forwardRef((props, ref) => <EditIcon {...props} ref={ref} />),
              Export: React.forwardRef((props, ref) => <ExportIcon {...props} ref={ref} />),
              Filter: React.forwardRef((props, ref) => <FilterIcon {...props} ref={ref} />),
              FirstPage: React.forwardRef((props, ref) => <FirstPageIcon {...props} ref={ref} />),
              LastPage: React.forwardRef((props, ref) => <LastPageIcon {...props} ref={ref} />),
              NextPage: React.forwardRef((props, ref) => <NextPageIcon {...props} ref={ref} />),
              PreviousPage: React.forwardRef((props, ref) => <PreviousPageIcon {...props} ref={ref} />),
              ResetSearch: React.forwardRef((props, ref) => <ResetSearchIcon {...props} ref={ref} />),
              Search: React.forwardRef((props, ref) => <SearchIcon {...props} ref={ref} />),
              SortArrow: React.forwardRef((props, ref) => <SortArrowIcon {...props} ref={ref} />),
              ThirdStateCheck: React.forwardRef((props, ref) => <ThirdStateCheckIcon {...props} ref={ref} />),
              ViewColumn: React.forwardRef((props, ref) => <ViewColumnIcon {...props} ref={ref} />),
            }}
            options={{
              loadingType: 'linear',
              padding: 'dense',
              search: false,
              toolbar: false,
              showTitle: false,
              header: true,
              paging: true,
              exportButton: true,
              grouping: true,
              filtering: badgeHistory.length > 10,
              emptyRowsWhenPaging: false,
              pageSize: 10,
              pageSizeOptions: [10, 25, 50, 100],
              actionsColumnIndex: -1,
              headerStyle: { padding: theme.spacing(1), backgroundColor: theme.palette.background.default },
              rowStyle: { backgroundColor: theme.palette.background.default },
              filterCellStyle: { padding: theme.spacing(1), backgroundColor: theme.palette.background.default },
              searchFieldStyle: { padding: theme.spacing(1), backgroundColor: theme.palette.background.default },
              actionsCellStyle: { padding: theme.spacing(1), backgroundColor: theme.palette.background.default },
              cellStyle: { padding: theme.spacing(1), backgroundColor: theme.palette.background.default },
            }}
            localization={{
              body: {
                emptyDataSourceMessage: 'No badge history to display.',
              },
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
};

BadgeHistory.propTypes = {
  classes: PropTypes.object.isRequired,
};

const styles = theme => ({
  root: {},
});

export default withStyles(styles, { withTheme: true })(withWidth()(observer(BadgeHistory)));
